import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * A customized button component for the dashboard
 * 
 * @param {string} variant - The button variant (contained, outlined, text)
 * @param {string} color - The button color (primary, secondary, success, error, etc.)
 * @param {boolean} isLoading - Whether the button is in loading state
 * @param {boolean} fullWidth - Whether the button should take full width
 * @param {string} startIcon - Icon to display at the start of the button
 * @param {string} endIcon - Icon to display at the end of the button
 * @param {string} size - The button size (small, medium, large)
 * @param {function} onClick - Click handler function
 * @param {boolean} disabled - Whether the button is disabled
 * @param {object} sx - Additional styles for the button
 * @param {string} animation - Animation type (scale, bounce, none)
 */
const DashboardButton = ({
  children,
  variant = 'contained',
  color = 'primary',
  isLoading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  size = 'medium',
  onClick,
  disabled = false,
  sx = {},
  animation = 'scale',
  ...props
}) => {
  // Prepare motion variants
  const motionVariants = {
    scale: {
      whileHover: { scale: 1.05 },
      whileTap: { scale: 0.95 },
    },
    bounce: {
      whileHover: { scale: 1.05, y: -3 },
      whileTap: { scale: 0.95, y: 2 },
    },
    none: {
      whileHover: {},
      whileTap: {},
    },
  };

  // Get animation settings
  const animationSettings = motionVariants[animation] || motionVariants.none;
  const MotionDiv = motion.div;


  return (
    <MotionDiv
      whileHover={animationSettings.whileHover}
      whileTap={animationSettings.whileTap}
      style={{ display: fullWidth ? 'block' : 'inline-block' }}
    >
      <Button
        variant={variant}
        color={color}
        fullWidth={fullWidth}
        startIcon={!isLoading && startIcon}
        endIcon={!isLoading && endIcon}
        size={size}
        onClick={onClick}
        disabled={disabled || isLoading}
        sx={{
          position: 'relative',
          ...sx,
        }}
        {...props}
      >
        {isLoading && (
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              color: 'currentColor',
            }}
          />
        )}
        <span style={{ opacity: isLoading ? 0 : 1 }}>{children}</span>
      </Button>
    </MotionDiv>
  );
};

export default DashboardButton;